import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Select,
  Typography,
  Space,
  message,
  Divider,
  Row,
  Col,
  Card,
  Tooltip,
  Modal,
  FormListFieldData,
  Upload,
  UploadFile,
  Skeleton,
  Alert,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  InboxOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useForm,
  Controller,
  useFieldArray,
  FieldArrayWithId,
  FieldErrors,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createVocab,
  editVocab,
  getVocab,
  searchMeanings,
  searchWords,
  deleteWordSynonym,
  deleteWordAntonym,
  deleteMeaning,
  deleteMeaningSynonym,
  deleteMeaningAntonym,
  deleteMeaningWordSynonym,
  deleteMeaningWordAntonym,
  deleteExample,
  deleteAlternativeCharacter,
  deleteAlternativePronounce,
  deleteMixWord,
  deleteCorruptedPronounce,
  getAllLatestIndexIds,
  downloadSound,
} from "../api/VocabAPI";
import {
  IVocab,
  IVocabCreateRequest,
  IVocabUpdateRequest,
  MeaningOption,
  SearchMeaningsResponse,
  SearchWordsResponse,
  VocabSchema,
  WordOption,
} from "../type/VocabManagementType";
import { getDictionaryList } from "../../dictionary/api/DictionaryAPI";
import { IDictionary } from "../../dictionary/type/DictionaryManagementType";
import { useMediaQuery } from "react-responsive";
import {
  generateInitialIndexId,
  getNextIndexId,
  IndexType,
} from "../../../utils/indexIdUtils";
import { ReactComponent as StopIcon } from "../../../assets/svg/stop-icon.svg";

const { Option } = Select;
const { Dragger } = Upload;

const defaultValues: IVocab = {
  hanji: "",
  roman: "",
  category: "",
  subCategory: "",
  etymology: "",
  dictionaryId: "",
  wordSynonym: [],
  wordAntonym: [],
  meaning: [],
  alternativeCharacter: [],
  alternativePronounce: [],
  mixWord: [],
  corruptedPronounce: [],
  newAccentField: [],
};

type FieldWithPossibleId = FormListFieldData & { id?: string };

const { Text, Title } = Typography;

const VocabAddEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isNewVocab = id === "new" || location.pathname === "/vocab/new";
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [wordSynonymSearchTerm, setWordSynonymSearchTerm] = useState("");
  const [wordAntonymSearchTerm, setWordAntonymSearchTerm] = useState("");
  const [meaningSynonymSearchTerm, setMeaningSynonymSearchTerm] = useState("");
  const [meaningAntonymSearchTerm, setMeaningAntonymSearchTerm] = useState("");
  const [meaningWordSynonymSearchTerm, setMeaningWordSynonymSearchTerm] =
    useState("");
  const [meaningWordAntonymSearchTerm, setMeaningWordAntonymSearchTerm] =
    useState("");
  const [soundFile, setSoundFile] = useState<UploadFile | null>(null);
  const [currentSoundUrl, setCurrentSoundUrl] = useState<string | null>(null);

  const [listDelete, setListDelete] = useState({});
  const [fileInfo, setFileInfo] = useState<{
    size: string;
    type: string;
  } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<IVocab>({
    resolver: zodResolver(VocabSchema),
    defaultValues,
  });

  const selectedDictionary = watch("dictionaryId");
  const isDictionarySelected = !!selectedDictionary;

  const tooltipTitle = "Please select a dictionary first";

  // Use useFieldArray for all array fields
  const {
    fields: meaningFields,
    append: appendMeaning,
    remove: removeMeaning,
  } = useFieldArray({ control, name: "meaning" });

  const {
    fields: wordSynonymFields,
    append: appendWordSynonym,
    remove: removeWordSynonym,
  } = useFieldArray({ control, name: "wordSynonym" });

  const {
    fields: wordAntonymFields,
    append: appendWordAntonym,
    remove: removeWordAntonym,
  } = useFieldArray({ control, name: "wordAntonym" });

  const {
    fields: alternativeCharacterFields,
    append: appendAlternativeCharacter,
    remove: removeAlternativeCharacter,
  } = useFieldArray({ control, name: "alternativeCharacter" });

  const {
    fields: alternativePronounceFields,
    append: appendAlternativePronounce,
    remove: removeAlternativePronounce,
  } = useFieldArray({ control, name: "alternativePronounce" });

  const {
    fields: mixWordFields,
    append: appendMixWord,
    remove: removeMixWord,
  } = useFieldArray({ control, name: "mixWord" });

  const {
    fields: corruptedPronounceFields,
    append: appendCorruptedPronounce,
    remove: removeCorruptedPronounce,
  } = useFieldArray({ control, name: "corruptedPronounce" });

  const {
    fields: newAccentFields,
    append: appendNewAccent,
    remove: removeNewAccent,
  } = useFieldArray({ control, name: "newAccentField" });

  const { data: vocabData, isLoading: isVocabLoading } = useQuery({
    queryKey: ["vocab", id],
    queryFn: () => getVocab(id),
    enabled: !isNewVocab,
  });

  const { data: dictionaryData, isLoading: isDictionaryLoading } = useQuery({
    queryKey: ["dictionary"],
    queryFn: () => getDictionaryList({ params: { pageSize: 100 } }),
  });

  const { data: wordSynonymOptions, isLoading: isWordSynonymSearchLoading } =
    useQuery<SearchWordsResponse>({
      queryKey: ["wordSearch", "wordSynonym", wordSynonymSearchTerm],
      queryFn: () => searchWords(wordSynonymSearchTerm, watch("dictionaryId")),
      enabled: !!wordSynonymSearchTerm,
    });

  const { data: wordAntonymOptions, isLoading: isWordAntonymSearchLoading } =
    useQuery<SearchWordsResponse>({
      queryKey: ["wordSearch", "wordAntonym", wordAntonymSearchTerm],
      queryFn: () => searchWords(wordAntonymSearchTerm, watch("dictionaryId")),
      enabled: !!wordAntonymSearchTerm,
    });

  const {
    data: meaningSynonymOptions,
    isLoading: isMeaningSynonymSearchLoading,
  } = useQuery<SearchMeaningsResponse>({
    queryKey: ["meaningSearch", "meaningSynonym", meaningSynonymSearchTerm],
    queryFn: () =>
      searchMeanings(meaningSynonymSearchTerm, watch("dictionaryId")),
    enabled: !!meaningSynonymSearchTerm && !!watch("dictionaryId"),
  });

  const {
    data: meaningAntonymOptions,
    isLoading: isMeaningAntonymSearchLoading,
  } = useQuery<SearchMeaningsResponse>({
    queryKey: ["meaningSearch", "meaningAntonym", meaningAntonymSearchTerm],
    queryFn: () =>
      searchMeanings(meaningAntonymSearchTerm, watch("dictionaryId")),
    enabled: !!meaningAntonymSearchTerm && !!watch("dictionaryId"),
  });

  const {
    data: meaningWordSynonymOptions,
    isLoading: isMeaningWordSynonymSearchLoading,
  } = useQuery<SearchWordsResponse>({
    queryKey: [
      "wordSearch",
      "meaningWordSynonym",
      meaningWordSynonymSearchTerm,
    ],
    queryFn: () =>
      searchWords(meaningWordSynonymSearchTerm, watch("dictionaryId")),
    enabled: !!meaningWordSynonymSearchTerm,
  });

  const {
    data: meaningWordAntonymOptions,
    isLoading: isMeaningWordAntonymSearchLoading,
  } = useQuery<SearchWordsResponse>({
    queryKey: [
      "wordSearch",
      "meaningWordAntonym",
      meaningWordAntonymSearchTerm,
    ],
    queryFn: () =>
      searchWords(meaningWordAntonymSearchTerm, watch("dictionaryId")),
    enabled: !!meaningWordAntonymSearchTerm,
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData | IVocab) => createVocab(data),
    onSuccess: () => {
      message.success(
        <div>
          <div>已成功創建詞目。</div>
          <div>Entry created successfully.</div>
        </div>
      );
      navigate("/vocab");
      queryClient.invalidateQueries({ queryKey: ["vocab"] });
    },
    onError: (error) => {
      console.error("Error creating vocab:", error);
      message.error(
        <div>
          <div>創建詞目失败：{error.message}</div>
          <div>Failed to create vocab: {error.message}</div>
        </div>
      );
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: FormData | IVocab) => {
      if (!id) throw new Error("ID is required for editing");
      return editVocab(id, data);
    },
    onSuccess: () => {
      message.success(
        <div>
          <div>已成功更新詞目。</div>
          <div>Entry updated successfully.</div>
        </div>
      );
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    },
    onError: (error) => {
      console.error("Error updating vocab:", error);
      message.error(
        <div>
          <div>更新詞目失败：{error.message}</div>
          <div>Failed to update entry: {error.message}</div>
        </div>
      );
    },
  });

  const [selectedDictionaryName, setSelectedDictionaryName] =
    useState<string>("");

  const { data: latestIndexIds, refetch: refetchLatestIndexIds } = useQuery({
    queryKey: ["latestIndexIds"],
    queryFn: () => getAllLatestIndexIds(selectedDictionaryName),
    enabled: false, // This keeps it as a lazy query
  });

  useEffect(() => {
    let formattedData: IVocab;

    if (vocabData) {
      formattedData = {
        hanji: vocabData.hanji || "",
        roman: vocabData.roman || "",
        category: vocabData.category || "",
        subCategory: vocabData.subCategory || "",
        etymology: vocabData.etymology || "",
        dictionaryId: vocabData.dictionaryId || "",
        wordSynonym:
          vocabData.wordSynonyms?.map((item: any) => ({
            id: item._id,
            relativeWordIndexId: item.relativeWordIndexId,
            relativeHanji: item.relativeHanji,
            relativeRoman: item.relativeRoman,
          })) || [],
        wordAntonym:
          vocabData.wordAntonyms?.map((item: any) => ({
            id: item._id,
            relativeWordIndexId: item.relativeWordIndexId,
            relativeHanji: item.relativeHanji,
            relativeRoman: item.relativeRoman,
          })) || [],
        meaning:
          vocabData.meanings?.map((item: any) => ({
            id: item._id,
            partOfSpeech: item.partOfSpeech || "",
            meaningChinese: item.meaningChinese || "",
            meaningEnglish: item.meaningEnglish || "",
            meaningHokkien: item.meaningHokkien || "",
            meaningTagalog: item.meaningTagalog || "",
            meaningIndonesia: item.meaningIndonesia || "",
            meaningMalay: item.meaningMalay || "",
            meaningSynonym:
              item.meaningSynonyms?.map((syn: any) => ({
                id: syn._id,
                relativeMeaningIndexId: syn.relativeMeaningIndexId,
                relativePartOfSpeech: syn.relativePartOfSpeech,
                relativeMeaningChinese: syn.relativeMeaningChinese,
                relativeMeaningHokkien: syn.relativeMeaningHokkien,
                relativeMeaningEnglish: syn.relativeMeaningEnglish,
                relativeHanji: syn.relativeHanji,
                relativeRoman: syn.relativeRoman,
              })) || [],
            meaningAntonym:
              item.meaningAntonyms?.map((ant: any) => ({
                id: ant._id,
                relativeMeaningIndexId: ant.relativeMeaningIndexId,
                relativePartOfSpeech: ant.relativePartOfSpeech,
                relativeMeaningChinese: ant.relativeMeaningChinese,
                relativeMeaningHokkien: ant.relativeMeaningHokkien,
                relativeMeaningEnglish: ant.relativeMeaningEnglish,
                relativeHanji: ant.relativeHanji,
                relativeRoman: ant.relativeRoman,
              })) || [],
            meaningWordSynonym:
              item.meaningWordSynonyms?.map((syn: any) => ({
                id: syn._id,
                relativeWordIndexId: syn.relativeWordIndexId,
                relativeHanji: syn.relativeHanji,
                relativeRoman: syn.relativeRoman,
              })) || [],
            meaningWordAntonym:
              item.meaningWordAntonyms?.map((ant: any) => ({
                id: ant._id,
                relativeWordIndexId: ant.relativeWordIndexId,
                relativeHanji: ant.relativeHanji,
                relativeRoman: ant.relativeRoman,
              })) || [],
            example:
              item.examples?.map((ex: any) => ({
                id: ex._id,
                exampleHanji: ex.exampleHanji || "",
                exampleRoman: ex.exampleRoman || "",
                exampleChinese: ex.exampleChinese || "",
                exampleEnglish: ex.exampleEnglish || "",
                exampleMalay: ex.exampleMalay || "",
                exampleTagalog: ex.exampleTagalog || "",
                exampleIndonesia: ex.exampleIndonesia || "",
              })) || [],
          })) || [],
        alternativeCharacter:
          vocabData.alternativeCharacters?.map((item: any) => ({
            id: item._id,
            alternativeCharacterHanji: item.alternativeCharacterHanji || "",
          })) || [],
        alternativePronounce:
          vocabData.alternativePronounces?.map((item: any) => ({
            id: item._id,
            alternativePronounceRoman: item.alternativePronounceRoman || "",
          })) || [],
        mixWord:
          vocabData.mixWords?.map((item: any) => ({
            id: item._id,
            mixWordRoman: item.mixWordRoman || "",
          })) || [],
        corruptedPronounce:
          vocabData.corruptedPronounces?.map((item: any) => ({
            id: item._id,
            corruptedPronounceRoman: item.corruptedPronounceRoman || "",
          })) || [],
        accent: vocabData.accents
          ? {
              id: vocabData.accents._id,
              accentIndexId: vocabData.accents.accentIndexId,
            }
          : undefined,
        newAccentField: vocabData.accents ? vocabData.accents.accents : [],
      };

      // Set the dictionary name when editing
      if (vocabData.dictionaryId) {
        const dictionary = dictionaryData?.results.find(
          (dict: IDictionary) => dict.id === vocabData.dictionaryId
        );
        if (dictionary) {
          setSelectedDictionaryName(dictionary.name);
        }
      }

      if (vocabData.recordURL) {
        setCurrentSoundUrl(vocabData.recordURL);
      }
    } else {
      formattedData = { ...defaultValues };
    }

    reset(formattedData);
  }, [vocabData, isNewVocab, reset, dictionaryData]);

  const handleSearch = (
    value: string,
    type:
      | "wordSynonym"
      | "wordAntonym"
      | "meaningSynonym"
      | "meaningAntonym"
      | "meaningWordSynonym"
      | "meaningWordAntonym"
  ) => {
    switch (type) {
      case "wordSynonym":
        setWordSynonymSearchTerm(value);
        break;
      case "wordAntonym":
        setWordAntonymSearchTerm(value);
        break;
      case "meaningSynonym":
        setMeaningSynonymSearchTerm(value);
        break;
      case "meaningAntonym":
        setMeaningAntonymSearchTerm(value);
        break;
      case "meaningWordSynonym":
        setMeaningWordSynonymSearchTerm(value);
        break;
      case "meaningWordAntonym":
        setMeaningWordAntonymSearchTerm(value);
        break;
    }
  };

  const handleRemoveOnBackend = async (
    id: string | undefined,
    type: string,
    index: number,
    parentIndex?: number
  ) => {
    if (!id) {
      message.error("ID is required for removing");
      return;
    }
    switch (type) {
      case "wordSynonym":
        await deleteWordSynonym(id);
        break;
      case "wordAntonym":
        await deleteWordAntonym(id);
        break;
      case "meaning":
        await deleteMeaning(id);
        break;
      case "meaningSynonym":
        await deleteMeaningSynonym(id);
        break;
      case "meaningAntonym":
        await deleteMeaningAntonym(id);
        break;
      case "meaningWordSynonym":
        await deleteMeaningWordSynonym(id);
        break;
      case "meaningWordAntonym":
        await deleteMeaningWordAntonym(id);
        break;
      case "example":
        await deleteExample(id);
        break;
      case "alternativeCharacter":
        await deleteAlternativeCharacter(id);
        break;
      case "alternativePronounce":
        await deleteAlternativePronounce(id);
        break;
      case "mixWord":
        await deleteMixWord(id);
        break;
      case "corruptedPronounce":
        await deleteCorruptedPronounce(id);
        break;
      default:
        throw new Error("Invalid remove type");
    }
  };

  const handleSoundUpload = (file: UploadFile) => {
    console.log("File uploaded:", file);
    const isWebm = file.type === "video/webm" || file.type === "audio/webm";
    if (!isWebm) {
      message.error("Only WebM files are allowed. Please select a WebM file.");
      return Upload.LIST_IGNORE;
    }

    // Check if file size is defined
    if (typeof file.size !== "number") {
      message.error(
        "We couldn't determine the file size. Please try uploading again."
      );
      return Upload.LIST_IGNORE;
    }

    // Check file size (50MB = 50 * 1024 * 1024 bytes)
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error(
        "The file is too large. Please choose a file smaller than 50MB."
      );
      return Upload.LIST_IGNORE;
    }

    setSoundFile(file);
    setCurrentSoundUrl(null);

    // Set file info
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    setFileInfo({
      size: `${fileSizeInMB} MB`,
      type: file.type || "Unknown",
    });

    return false; // Prevent automatic upload
  };

  const handleSoundRemove = () => {
    setSoundFile(null);
    setCurrentSoundUrl(null);
    setFileInfo(null);
  };

  const handlePlaySound = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch((error) => {
          console.error("Error playing audio:", error);
          message.error(
            "Failed to play audio. Please check the file format and try again."
          );
        });
        setIsPlaying(true);
      }
    } else {
      message.error("Audio player is not available.");
    }
  };

  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const handleEnded = () => setIsPlaying(false);
      audioElement.addEventListener("ended", handleEnded);
      return () => {
        audioElement.removeEventListener("ended", handleEnded);
      };
    }
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadSound = async () => {
    if (id && currentSoundUrl) {
      try {
        setIsDownloading(true);
        const { data, filename } = await downloadSound(id);
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        message.success("Download started. Check your downloads folder.");

        // Clean up the object URL
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading sound:", error);
        message.error("Failed to download sound file.");
      } finally {
        setIsDownloading(false);
      }
    } else {
      message.error("No audio file available for download.");
    }
  };

  const prepareData = (data: any, isEditing: boolean): any => {
    if (Array.isArray(data)) {
      return data.map((item) => prepareData(item, isEditing));
    } else if (typeof data === "object" && data !== null) {
      const newObj: any = {};
      for (const [key, value] of Object.entries(data)) {
        if (value !== null && value !== undefined) {
          // Skip dictionaryId when editing
          if (isEditing && key === "dictionaryId") continue;

          const cleaned = prepareData(value, isEditing);
          if (Array.isArray(cleaned) && cleaned.length > 0) {
            newObj[key] = cleaned;
          } else if (
            typeof cleaned === "object" &&
            Object.keys(cleaned).length > 0
          ) {
            newObj[key] = cleaned;
          } else if (!Array.isArray(cleaned) && typeof cleaned !== "object") {
            newObj[key] = cleaned;
          }
        }
      }
      return newObj;
    }
    return data;
  };

  const removeEmptyValues = (obj: any): any => {
    if (Array.isArray(obj)) {
      const filteredArray = obj
        .map(removeEmptyValues)
        .filter((item) => item != null && item !== "");
      return filteredArray.length > 0 ? filteredArray : undefined;
    } else if (typeof obj === "object" && obj !== null) {
      const newObj: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = removeEmptyValues(value);
        if (cleanedValue != null && cleanedValue !== "") {
          newObj[key] = cleanedValue;
        }
      }
      return Object.keys(newObj).length > 0 ? newObj : undefined;
    }
    return obj != null && obj !== "" ? obj : undefined;
  };

  const [duplicateError, setDuplicateError] = useState<
    { type: string; value: string; field: string }[] | null
  >(null);

  // Function to check for duplicates
  const checkForDuplicates = (values: IVocab) => {
    const allWords = new Set<string>();
    const allMeanings = new Set<string>();
    const allAccents = new Set<string>();
    const duplicates: { type: string; value: string; field: string }[] = [];

    const addToSet = (
      set: Set<string>,
      value: string,
      type: string,
      field: string
    ) => {
      // Ignore empty values
      if (!value.trim()) return;

      if (set.has(value)) {
        duplicates.push({ type, value, field });
      } else {
        set.add(value);
      }
    };

    // Check word synonyms and antonyms
    values.wordSynonym?.forEach((syn, index) =>
      addToSet(
        allWords,
        syn.relativeWordIndexId ?? "",
        "Word Synonym",
        `Word Synonyms (Item ${index + 1})`
      )
    );
    values.wordAntonym?.forEach((ant, index) =>
      addToSet(
        allWords,
        ant.relativeWordIndexId ?? "",
        "Word Antonym",
        `Word Antonyms (Item ${index + 1})`
      )
    );

    // Check meanings and their related fields
    values.meaning?.forEach((meaning, meaningIndex) => {
      meaning.meaningSynonym?.forEach((syn, index) =>
        addToSet(
          allMeanings,
          syn.relativeMeaningIndexId ?? "",
          "Meaning Synonym",
          `Meaning ${meaningIndex + 1} - Synonyms (Item ${index + 1})`
        )
      );
      meaning.meaningAntonym?.forEach((ant, index) =>
        addToSet(
          allMeanings,
          ant.relativeMeaningIndexId ?? "",
          "Meaning Antonym",
          `Meaning ${meaningIndex + 1} - Antonyms (Item ${index + 1})`
        )
      );
      meaning.meaningWordSynonym?.forEach((syn, index) =>
        addToSet(
          allWords,
          syn.relativeWordIndexId ?? "",
          "Meaning Word Synonym",
          `Meaning ${meaningIndex + 1} - Word Synonyms (Item ${index + 1})`
        )
      );
      meaning.meaningWordAntonym?.forEach((ant, index) =>
        addToSet(
          allWords,
          ant.relativeWordIndexId ?? "",
          "Meaning Word Antonym",
          `Meaning ${meaningIndex + 1} - Word Antonyms (Item ${index + 1})`
        )
      );
    });

    // Check accents input
    values.newAccentField?.forEach((item) =>
      addToSet(allAccents, item.region ?? "", "Accent", "Accent")
    );

    return duplicates;
  };

  const generateNewIndexIds = (
    latestIndexIds: Record<IndexType, string>,
    dictionaryName: string,
    isNewVocab: boolean,
    formValues: IVocab
  ) => {
    const newIndexIds: Partial<IVocab> = {};

    const generateIndexId = (type: IndexType, existingId?: string) => {
      if (existingId) return existingId;
      const newId = latestIndexIds[type]
        ? getNextIndexId(latestIndexIds[type])
        : generateInitialIndexId(dictionaryName, type);
      latestIndexIds[type] = newId; // Update the latest ID for the next item
      return newId;
    };

    if (isNewVocab) {
      newIndexIds.wordIndexId = generateIndexId("word");

      // Generate new index IDs for other fields
      newIndexIds.meaning = formValues.meaning?.map((meaning) => ({
        meaningIndexId: generateIndexId("meaning"),
        ...meaning,
        meaningSynonym: meaning.meaningSynonym?.map((syn) => ({
          ...syn,
          meaningSynonymIndexId: generateIndexId("meaningSynonym"),
        })),
        meaningAntonym: meaning.meaningAntonym?.map((ant) => ({
          ...ant,
          meaningAntonymIndexId: generateIndexId("meaningAntonym"),
        })),
        meaningWordSynonym: meaning.meaningWordSynonym?.map((syn) => ({
          ...syn,
          meaningWordSynonymIndexId: generateIndexId("meaningWordSynonym"),
        })),
        meaningWordAntonym: meaning.meaningWordAntonym?.map((ant) => ({
          ...ant,
          meaningWordAntonymIndexId: generateIndexId("meaningWordAntonym"),
        })),
        example: meaning.example?.map((ex) => ({
          ...ex,
          exampleIndexId: generateIndexId("example"),
        })),
      }));

      newIndexIds.wordSynonym = formValues.wordSynonym?.map((syn) => ({
        ...syn,
        wordSynonymIndexId: generateIndexId("wordSynonym"),
      }));

      newIndexIds.wordAntonym = formValues.wordAntonym?.map((ant) => ({
        ...ant,
        wordAntonymIndexId: generateIndexId("wordAntonym"),
      }));

      newIndexIds.alternativeCharacter = formValues.alternativeCharacter?.map(
        (char) => ({
          ...char,
          alternativeCharacterIndexId: generateIndexId("alternativeCharacter"),
        })
      );

      newIndexIds.alternativePronounce = formValues.alternativePronounce?.map(
        (pron) => ({
          ...pron,
          alternativePronounceIndexId: generateIndexId("alternativePronounce"),
        })
      );

      newIndexIds.mixWord = formValues.mixWord?.map((mix) => ({
        ...mix,
        mixWordIndexId: generateIndexId("mixWord"),
      }));

      newIndexIds.corruptedPronounce = formValues.corruptedPronounce?.map(
        (corrupt) => ({
          ...corrupt,
          corruptedPronounceIndexId: generateIndexId("corruptedPronounce"),
        })
      );

      // Check if any accent field is non-empty
      const hasNonEmptyAccent = !!formValues.newAccentField;

      if (hasNonEmptyAccent) {
        newIndexIds.accent = {
          accentIndexId: generateIndexId("accent"),
        };
      } else {
        newIndexIds.accent = {}; // Keep the accent object without an accentIndexId
      }
    } else {
      // For editing, preserve existing index IDs and generate new ones for newly added items
      newIndexIds.wordIndexId = formValues.wordIndexId;
      newIndexIds.meaning = formValues.meaning?.map((meaning) => ({
        ...meaning,
        meaningIndexId: generateIndexId("meaning", meaning.meaningIndexId),
        meaningSynonym: meaning.meaningSynonym?.map((syn) => ({
          ...syn,
          meaningSynonymIndexId: generateIndexId(
            "meaningSynonym",
            syn.meaningSynonymIndexId
          ),
        })),
        meaningAntonym: meaning.meaningAntonym?.map((ant) => ({
          ...ant,
          meaningAntonymIndexId: generateIndexId(
            "meaningAntonym",
            ant.meaningAntonymIndexId
          ),
        })),
        meaningWordSynonym: meaning.meaningWordSynonym?.map((syn) => ({
          ...syn,
          meaningWordSynonymIndexId: generateIndexId(
            "meaningWordSynonym",
            syn.meaningWordSynonymIndexId
          ),
        })),
        meaningWordAntonym: meaning.meaningWordAntonym?.map((ant) => ({
          ...ant,
          meaningWordAntonymIndexId: generateIndexId(
            "meaningWordAntonym",
            ant.meaningWordAntonymIndexId
          ),
        })),
        example: meaning.example?.map((ex) => ({
          ...ex,
          exampleIndexId: generateIndexId("example", ex.exampleIndexId),
        })),
      }));
      newIndexIds.wordSynonym = formValues.wordSynonym?.map((syn) => ({
        ...syn,
        wordSynonymIndexId: generateIndexId(
          "wordSynonym",
          syn.wordSynonymIndexId
        ),
      }));
      newIndexIds.wordAntonym = formValues.wordAntonym?.map((ant) => ({
        ...ant,
        wordAntonymIndexId: generateIndexId(
          "wordAntonym",
          ant.wordAntonymIndexId
        ),
      }));
      newIndexIds.alternativeCharacter = formValues.alternativeCharacter?.map(
        (char) => ({
          ...char,
          alternativeCharacterIndexId: generateIndexId(
            "alternativeCharacter",
            char.alternativeCharacterIndexId
          ),
        })
      );
      newIndexIds.alternativePronounce = formValues.alternativePronounce?.map(
        (pron) => ({
          ...pron,
          alternativePronounceIndexId: generateIndexId(
            "alternativePronounce",
            pron.alternativePronounceIndexId
          ),
        })
      );
      newIndexIds.mixWord = formValues.mixWord?.map((mix) => ({
        ...mix,
        mixWordIndexId: generateIndexId("mixWord", mix.mixWordIndexId),
      }));
      newIndexIds.corruptedPronounce = formValues.corruptedPronounce?.map(
        (corrupt) => ({
          ...corrupt,
          corruptedPronounceIndexId: generateIndexId(
            "corruptedPronounce",
            corrupt.corruptedPronounceIndexId
          ),
        })
      );
      newIndexIds.accent = {
        ...formValues.accent,
        accentIndexId: generateIndexId(
          "accent",
          formValues.accent?.accentIndexId
        ),
      };
    }

    return newIndexIds;
  };

  // Modify the onSubmit function
  const onSubmit = async (values: IVocab) => {
    console.log("value: ", values);

    const duplicates = checkForDuplicates(values);
    if (duplicates.length > 0) {
      setDuplicateError(duplicates);
      return;
    }
    setDuplicateError(null);

    // Remove empty values
    const cleanedValues = removeEmptyValues(values);

    console.log("cleanedValues", cleanedValues);

    try {
      // Fetch the latest index IDs before submission
      const { data: fetchedLatestIndexIds } = await refetchLatestIndexIds();

      if (!fetchedLatestIndexIds) {
        message.error("Unable to generate index IDs. Please try again.");
        return;
      }

      // Generate new index IDs for each field that requires one
      const newIndexIds = generateNewIndexIds(
        fetchedLatestIndexIds,
        selectedDictionaryName,
        isNewVocab,
        cleanedValues // Pass the cleaned form values
      );

      // Merge the new index IDs with the cleaned values
      const mergedValues = { ...cleanedValues, ...newIndexIds };

      // Prepare data, excluding dictionaryId when editing
      const preparedData: IVocab = prepareData(mergedValues, !isNewVocab);

      // Ensure _id is included for accent if it exists
      if (preparedData.accent && vocabData?.accents?._id) {
        preparedData.accent.id = vocabData.accents._id;
      }

      if (preparedData.newAccentField) {
        preparedData.accent = {
          ...preparedData.accent,
          accents: preparedData.newAccentField,
        };
        delete preparedData.newAccentField;
      }

      // Final check to remove any remaining undefined values
      const finalData = Object.fromEntries(
        Object.entries(preparedData).filter(([_, v]) => v != null)
      );

      // Use FormData
      const formData = new FormData();

      // Append all the existing form data
      Object.entries(finalData).forEach(([key, value]) => {
        if (key !== "sound") {
          // If the value is an array or object, convert it to a JSON string
          if (Array.isArray(value) || typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value as string);
          }
        }
      });

      // Append the sound file if it exists
      if (soundFile && soundFile instanceof File) {
        formData.append("sound", soundFile, soundFile.name);
      } else if (
        soundFile &&
        "originFileObj" in soundFile &&
        soundFile.originFileObj instanceof File
      ) {
        formData.append(
          "sound",
          soundFile.originFileObj,
          soundFile.originFileObj.name
        );
      }

      if (isNewVocab) {
        createMutation.mutate(formData);
      } else {
        if (!id) {
          message.error("ID is required for editing");
          return;
        }
        editMutation.mutate(formData);
      }
    } catch (error) {
      console.error("Error fetching latest index IDs:", error);
      message.error("Failed to prepare submission. Please try again.");
    }
  };

  const onError = (errors: FieldErrors<IVocab>) => {
    console.log("Form Validation Errors:", errors);
    message.error(
      <div>
        <div>無法提交。請檢查是否填寫必填欄位。</div>
        <div>Submission failed. Please fill in all required fields.</div>
      </div>
    );
  };

  const handleFormSubmit = async (e: React.BaseSyntheticEvent) => {
    // Log form values regardless of validation
    const formValues = getValues();
    console.log("Form values (including invalid data):", formValues);

    if (Object.keys(listDelete).length > 0) {
      await Promise.all(
        Object.keys(listDelete).map((item, idx) =>
          handleRemoveOnBackend(item, Object.values(listDelete)[idx] as any, 0)
        )
      );
    }

    // Attempt to validate and submit
    handleSubmit(onSubmit, onError)(e);
  };

  // Add useEffect to clear the error when form values change
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (duplicateError) {
        setDuplicateError(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, duplicateError]);

  if ((!isNewVocab && isVocabLoading) || isDictionaryLoading) {
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px" }}>
        <Skeleton.Input style={{ width: 200, marginBottom: 16 }} active />
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Skeleton active paragraph={{ rows: 6 }} />
            <Skeleton.Input
              style={{ width: "100%", height: 32, marginTop: 16 }}
              active
            />
            <Skeleton.Input
              style={{ width: "100%", height: 32, marginTop: 16 }}
              active
            />
          </Col>
          {!isMobile && (
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
              <Skeleton.Input
                style={{
                  width: "100%",
                  height: 32,
                  marginTop: 16,
                }}
                active
              />
              <Skeleton.Input
                style={{
                  width: "100%",
                  height: 32,
                  marginTop: 16,
                }}
                active
              />
            </Col>
          )}
        </Row>
        {isMobile && (
          <div style={{ marginTop: 24 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
            <Skeleton.Input
              style={{ width: "100%", height: 32, marginTop: 16 }}
              active
            />
            <Skeleton.Input
              style={{ width: "100%", height: 32, marginTop: 16 }}
              active
            />
          </div>
        )}
      </div>
    );
  }

  const renderHeading = ({ title }: { title: string }) => {
    return (
      <>
        <Text
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#7E5435",
          }}
        >
          {title}
        </Text>
        <div
          style={{
            height: 8,
          }}
        />
      </>
    );
  };

  return (
    <Form
      layout={"vertical"}
      onFinish={handleFormSubmit}
      style={{ maxWidth: 1000, margin: "0 auto" }}
      autoComplete="off"
    >
      <Title level={4}>
        {isNewVocab
          ? "創建新詞目 / Create a new entry"
          : "編輯詞目 / Edit entry"}
      </Title>

      {!isNewVocab && vocabData?.wordIndexId && (
        <Text strong>
          詞目 ID / Entry ID: <Text code>{vocabData.wordIndexId}</Text>
        </Text>
      )}
      <div style={{ height: 8 }} />
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          {renderHeading({ title: "詞目 / Entry" })}
          {/* Dictionary */}
          <Form.Item label="來源 / Source" required={isNewVocab}>
            <Controller
              name="dictionaryId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  disabled={!isNewVocab}
                  onChange={(value) => {
                    field.onChange(value);
                    const dictionary = dictionaryData?.results.find(
                      (dict: IDictionary) => dict.id === value
                    );
                    if (dictionary) {
                      setSelectedDictionaryName(dictionary.name);
                    }
                  }}
                >
                  {dictionaryData?.results.map((dict: IDictionary) => (
                    <Option key={dict.id} value={dict.id}>
                      {dict.name}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.dictionaryId && <span>{errors.dictionaryId.message}</span>}
          </Form.Item>
          <Form.Item label="漢字 / Chinese character">
            <Controller
              name="hanji"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.hanji && <span>{errors.hanji.message}</span>}
          </Form.Item>

          <Form.Item label="羅馬字 / Romanisation" required>
            <Controller
              name="roman"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.roman && <span>{errors.roman.message}</span>}
          </Form.Item>

          <Form.Item label="詞目類型 / Entry type">
            <Controller
              name="category"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.category && <span>{errors.category.message}</span>}
          </Form.Item>

          <Form.Item label="分類 / Category">
            <Controller
              name="subCategory"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.subCategory && <span>{errors.subCategory.message}</span>}
          </Form.Item>

          <Form.Item label="語源 (Etymology)">
            <Controller
              name="etymology"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.etymology && <span>{errors.etymology.message}</span>}
          </Form.Item>
          {/* Alternative Character */}
          <Form.List name="alternativeCharacter">
            {() => (
              <>
                <Text>異用字 / Alternative character(s)</Text>
                <div style={{ height: 8 }} />
                {alternativeCharacterFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom:
                        index === alternativeCharacterFields.length - 1
                          ? 16
                          : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item
                          required={false}
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          <Controller
                            name={`alternativeCharacter.${index}.alternativeCharacterHanji`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(
                              `alternativeCharacter.${index}`
                            ) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "alternativeCharacter",
                              });

                              removeAlternativeCharacter(index);
                            } else {
                              removeAlternativeCharacter(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < alternativeCharacterFields.length - 1 && (
                      <div style={{ height: 8 }} /> /* Spacing between fields */
                    )}
                  </div>
                ))}
                {/* Spacing before the Add button */}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() =>
                      appendAlternativeCharacter({
                        alternativeCharacterHanji: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    添加異用字 / Add an alternative character
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Alternative Pronounce */}
          <Form.List name="alternativePronounce">
            {() => (
              <>
                <Text>又唸作 / Alternative pronunciation(s)</Text>
                <div style={{ height: 8 }} />
                {alternativePronounceFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom:
                        index === alternativePronounceFields.length - 1
                          ? 16
                          : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item
                          required={false}
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          <Controller
                            name={`alternativePronounce.${index}.alternativePronounceRoman`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(
                              `alternativePronounce.${index}`
                            ) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "alternativePronounce",
                              });
                              removeAlternativePronounce(index);
                            } else {
                              removeAlternativePronounce(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < alternativePronounceFields.length - 1 && (
                      <div style={{ height: 8 }} />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() =>
                      appendAlternativePronounce({
                        alternativePronounceRoman: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    添加又唸作 / Add an alternative pronunciation
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Mix Word */}
          <Form.List name="mixWord">
            {() => (
              <>
                <Text>合音唸作 / Contraction(s)</Text>
                <div style={{ height: 8 }} />
                {mixWordFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom: index === mixWordFields.length - 1 ? 16 : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item required={false} style={{ marginBottom: 0 }}>
                          <Controller
                            name={`mixWord.${index}.mixWordRoman`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(`mixWord.${index}`) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "mixWord",
                              });
                              removeMixWord(index);
                            } else {
                              removeMixWord(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < mixWordFields.length - 1 && (
                      <div style={{ height: 8 }} />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => appendMixWord({ mixWordRoman: "" })}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加合音唸作/ Add a contraction
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Corrupted Pronounce */}
          <Form.List name="corruptedPronounce">
            {() => (
              <>
                <Text>俗唸作 / Mispronunciation(s)</Text>
                <div style={{ height: 8 }} />
                {corruptedPronounceFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom:
                        index === corruptedPronounceFields.length - 1 ? 16 : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item
                          required={false}
                          style={{
                            marginBottom: 0,
                          }}
                        >
                          <Controller
                            name={`corruptedPronounce.${index}.corruptedPronounceRoman`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(
                              `corruptedPronounce.${index}`
                            ) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "corruptedPronounce",
                              });
                              removeCorruptedPronounce(index);
                            } else {
                              removeCorruptedPronounce(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < corruptedPronounceFields.length - 1 && (
                      <div style={{ height: 8 }} />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() =>
                      appendCorruptedPronounce({
                        corruptedPronounceRoman: "",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    添加俗唸作 / Add a mispronunciation
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Meaning */}
          {renderHeading({ title: "義項 / Definition" })}
          {meaningFields.map((field, meaningIndex) => (
            <Card
              key={field.id}
              style={{
                marginBottom: 16,
              }}
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{`義項 ${meaningIndex + 1} / Definition ${meaningIndex + 1}`}</span>
                  <Button
                    type="text"
                    icon={<MinusCircleOutlined />}
                    onClick={() => {
                      const fieldValue = watch(
                        `meaning.${meaningIndex}`
                      ) as any;
                      if (fieldValue && fieldValue.id) {
                        setListDelete({
                          ...listDelete,
                          [fieldValue.id]: "meaning",
                        });
                        removeMeaning(meaningIndex);
                      } else {
                        removeMeaning(meaningIndex);
                      }
                    }}
                  />
                </div>
              }
            >
              <Form.Item label="詞性 / Part of speech">
                <Controller
                  name={`meaning.${meaningIndex}.partOfSpeech`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="福建文義項 (Hokkien definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningHokkien`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="華文義項 (Mandarin definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningChinese`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="英文義項 (English definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningEnglish`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="馬來文義項 (Malay definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningMalay`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="印尼文義項 (Indonesian definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningIndonesia`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
              <Form.Item label="菲律賓文義項 (Filipino definition)">
                <Controller
                  name={`meaning.${meaningIndex}.meaningTagalog`}
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>

              <div style={{ height: "12px" }} />

              {/* Meaning Synonym */}
              <Form.List
                name={`meaning.${meaningIndex}.meaningSynonym`}
                initialValue={getValues(
                  `meaning.${meaningIndex}.meaningSynonym`
                )}
              >
                {(synonymFields, { add, remove }) => (
                  <>
                    <Text>
                      義項-義項近義 / Definition-definition synonym(s)
                    </Text>
                    <div style={{ height: 8 }} />
                    {(synonymFields as FieldWithPossibleId[]).map(
                      (synonymField, synonymIndex) => (
                        <div
                          key={synonymField.fieldKey}
                          style={{
                            marginBottom:
                              synonymIndex === synonymFields.length - 1
                                ? 16
                                : 0,
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col flex="auto">
                              <Form.Item
                                required={false}
                                style={{
                                  marginBottom: 0,
                                }}
                              >
                                <Controller
                                  name={`meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningIndexId`}
                                  control={control}
                                  render={({ field }) => {
                                    const relativeMeaningChinese = watch(
                                      `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningChinese`
                                    );
                                    const relativeMeaningEnglish = watch(
                                      `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningEnglish`
                                    );
                                    const relativeMeaningHokkien = watch(
                                      `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningHokkien`
                                    );

                                    const currentOption = field.value
                                      ? {
                                          meaningIndexId: field.value,
                                          meaningChinese:
                                            relativeMeaningChinese || "",
                                          meaningEnglish:
                                            relativeMeaningEnglish || "",
                                          meaningHokkien:
                                            relativeMeaningHokkien || "",
                                        }
                                      : null;

                                    const allOptions = currentOption
                                      ? [
                                          currentOption,
                                          ...(meaningSynonymOptions?.results.filter(
                                            (opt) =>
                                              opt.meaningIndexId !== field.value
                                          ) || []),
                                        ]
                                      : meaningSynonymOptions?.results || [];

                                    return (
                                      <Select
                                        {...field}
                                        showSearch
                                        placeholder="Search for definition-definition synonym"
                                        onSearch={(value) =>
                                          handleSearch(value, "meaningSynonym")
                                        }
                                        onChange={(value) => {
                                          const selectedOption =
                                            allOptions.find(
                                              (opt) =>
                                                opt.meaningIndexId === value
                                            );

                                          field.onChange(value);

                                          if (selectedOption) {
                                            setValue(
                                              `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningChinese`,
                                              selectedOption.meaningChinese
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningEnglish`,
                                              selectedOption.meaningEnglish
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}.relativeMeaningHokkien`,
                                              selectedOption.meaningHokkien
                                            );
                                          }
                                        }}
                                        filterOption={false}
                                        loading={isMeaningSynonymSearchLoading}
                                        optionLabelProp="label"
                                        disabled={!isDictionarySelected}
                                      >
                                        {allOptions.map((option) => (
                                          <Option
                                            key={option.meaningIndexId}
                                            value={option.meaningIndexId}
                                            label={`${option.meaningChinese || option.meaningHokkien || option.meaningEnglish} (${option.meaningIndexId})`}
                                          >
                                            <span>{option.meaningChinese}</span>
                                            <br />
                                            <span>
                                              {option.meaningEnglish}{" "}
                                              {option.meaningHokkien} (
                                              {option.meaningIndexId})
                                            </span>
                                          </Option>
                                        ))}
                                      </Select>
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => {
                                  const fieldValue = watch(
                                    `meaning.${meaningIndex}.meaningSynonym.${synonymIndex}`
                                  ) as any;
                                  if (fieldValue && fieldValue.id) {
                                    setListDelete({
                                      ...listDelete,
                                      [fieldValue.id]: "meaningSynonym",
                                    });
                                    remove(synonymIndex);
                                    setValue(
                                      `meaning.${meaningIndex}.meaningSynonym`,
                                      watch(
                                        `meaning.${meaningIndex}.meaningSynonym`
                                      )?.filter(
                                        (item) => item.id !== fieldValue.id
                                      )
                                    );
                                    console.log(
                                      "watch: ",
                                      watch(
                                        `meaning.${meaningIndex}.meaningSynonym`
                                      )
                                    );
                                  } else {
                                    remove(synonymIndex);
                                  }
                                }}
                              />
                            </Col>
                          </Row>
                          {synonymIndex < synonymFields.length - 1 && (
                            <div style={{ height: 8 }} />
                          )}
                        </div>
                      )
                    )}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add({})}
                        block
                        icon={<PlusOutlined />}
                        disabled={!isDictionarySelected}
                      >
                        添加 / Add
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              {/* Meaning Antonym */}
              <Form.List
                name={`meaning.${meaningIndex}.meaningAntonym`}
                initialValue={getValues(
                  `meaning.${meaningIndex}.meaningAntonym`
                )}
              >
                {(antonymFields, { add, remove }) => (
                  <>
                    <Text>
                      義項-義項反義 / Definition-definition antonym(s)
                    </Text>
                    <div style={{ height: 8 }} />
                    {(antonymFields as FieldWithPossibleId[]).map(
                      (antonymField, antonymIndex) => (
                        <div
                          key={antonymField.fieldKey}
                          style={{
                            marginBottom:
                              antonymIndex === antonymFields.length - 1
                                ? 16
                                : 0,
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col flex="auto">
                              <Form.Item
                                required={false}
                                style={{
                                  marginBottom: 0,
                                }}
                              >
                                <Controller
                                  name={`meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningIndexId`}
                                  control={control}
                                  render={({ field }) => {
                                    const relativeMeaningChinese = watch(
                                      `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningChinese`
                                    );
                                    const relativeMeaningEnglish = watch(
                                      `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningEnglish`
                                    );
                                    const relativeMeaningHokkien = watch(
                                      `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningHokkien`
                                    );

                                    const currentOption = field.value
                                      ? {
                                          meaningIndexId: field.value,
                                          meaningChinese:
                                            relativeMeaningChinese || "",
                                          meaningEnglish:
                                            relativeMeaningEnglish || "",
                                          meaningHokkien:
                                            relativeMeaningHokkien || "",
                                        }
                                      : null;

                                    const allOptions = currentOption
                                      ? [
                                          currentOption,
                                          ...(meaningAntonymOptions?.results.filter(
                                            (opt) =>
                                              opt.meaningIndexId !== field.value
                                          ) || []),
                                        ]
                                      : meaningAntonymOptions?.results || [];

                                    return (
                                      <Select
                                        {...field}
                                        showSearch
                                        placeholder="Search for definition-definition antonym"
                                        onSearch={(value) =>
                                          handleSearch(value, "meaningAntonym")
                                        }
                                        onChange={(value) => {
                                          const selectedOption =
                                            allOptions.find(
                                              (opt) =>
                                                opt.meaningIndexId === value
                                            );

                                          field.onChange(value);

                                          if (selectedOption) {
                                            setValue(
                                              `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningChinese`,
                                              selectedOption.meaningChinese
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningEnglish`,
                                              selectedOption.meaningEnglish
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}.relativeMeaningHokkien`,
                                              selectedOption.meaningHokkien
                                            );
                                          }
                                        }}
                                        filterOption={false}
                                        loading={isMeaningAntonymSearchLoading}
                                        optionLabelProp="label"
                                        disabled={!isDictionarySelected}
                                      >
                                        {allOptions.map((option) => (
                                          <Option
                                            key={option.meaningIndexId}
                                            value={option.meaningIndexId}
                                            label={`${option.meaningChinese?.trim() || option.meaningHokkien?.trim() || option.meaningEnglish?.trim()} (${option.meaningIndexId})`}
                                          >
                                            <span>{option.meaningChinese}</span>
                                            <br />
                                            <span>
                                              {option.meaningEnglish}{" "}
                                              {option.meaningHokkien} (
                                              {option.meaningIndexId})
                                            </span>
                                          </Option>
                                        ))}
                                      </Select>
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => {
                                  const fieldValue = watch(
                                    `meaning.${meaningIndex}.meaningAntonym.${antonymIndex}`
                                  ) as any;
                                  if (fieldValue && fieldValue.id) {
                                    setListDelete({
                                      ...listDelete,
                                      [fieldValue.id]: "meaningAntonym",
                                    });
                                    remove(antonymIndex);
                                    setValue(
                                      `meaning.${meaningIndex}.meaningAntonym`,
                                      watch(
                                        `meaning.${meaningIndex}.meaningAntonym`
                                      )?.filter(
                                        (item) => item.id !== fieldValue.id
                                      )
                                    );
                                  } else {
                                    remove(antonymIndex);
                                  }
                                }}
                              />
                            </Col>
                          </Row>
                          {antonymIndex < antonymFields.length - 1 && (
                            <div style={{ height: 8 }} />
                          )}
                        </div>
                      )
                    )}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            relativeMeaningIndexId: "",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                        disabled={!isDictionarySelected}
                      >
                        添加 / Add
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              {/* Meaning Word Synonym */}
              <Form.List
                name={`meaning.${meaningIndex}.meaningWordSynonym`}
                initialValue={getValues(
                  `meaning.${meaningIndex}.meaningWordSynonym`
                )}
              >
                {(wordSynonymFields, { add, remove }) => (
                  <>
                    <Text>義項-詞目近義 / Definition-entry synonym(s)</Text>
                    <div style={{ height: 8 }} />
                    {(wordSynonymFields as FieldWithPossibleId[]).map(
                      (wordSynonymField, wordSynonymIndex) => (
                        <div
                          key={wordSynonymField.fieldKey}
                          style={{
                            marginBottom:
                              wordSynonymIndex === wordSynonymFields.length - 1
                                ? 16
                                : 0,
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col flex="auto">
                              <Form.Item
                                required={false}
                                style={{
                                  marginBottom: 0,
                                }}
                              >
                                <Controller
                                  name={`meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}.relativeWordIndexId`}
                                  control={control}
                                  render={({ field }) => {
                                    const relativeHanji = watch(
                                      `meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}.relativeHanji`
                                    );
                                    const relativeRoman = watch(
                                      `meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}.relativeRoman`
                                    );

                                    const currentOption = field.value
                                      ? {
                                          wordIndexId: field.value,
                                          hanji: relativeHanji || "",
                                          roman: relativeRoman || "",
                                        }
                                      : null;

                                    const allOptions = currentOption
                                      ? [
                                          currentOption,
                                          ...(meaningWordSynonymOptions?.results.filter(
                                            (opt: WordOption) =>
                                              opt.wordIndexId !== field.value
                                          ) || []),
                                        ]
                                      : meaningWordSynonymOptions?.results ||
                                        [];
                                    return (
                                      <Select
                                        {...field}
                                        showSearch
                                        placeholder="Search for definition-entry synonym"
                                        onSearch={(value) =>
                                          handleSearch(
                                            value,
                                            "meaningWordSynonym"
                                          )
                                        }
                                        onChange={(value) => {
                                          const selectedOption =
                                            allOptions.find(
                                              (opt) => opt.wordIndexId === value
                                            );

                                          field.onChange(value);

                                          if (selectedOption) {
                                            setValue(
                                              `meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}.relativeHanji`,
                                              selectedOption.hanji
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}.relativeRoman`,
                                              selectedOption.roman
                                            );
                                          }
                                        }}
                                        filterOption={false}
                                        loading={
                                          isMeaningWordSynonymSearchLoading
                                        }
                                        optionLabelProp="label"
                                        disabled={!isDictionarySelected}
                                      >
                                        {allOptions.map((option) => (
                                          <Option
                                            key={option.wordIndexId}
                                            value={option.wordIndexId}
                                            label={`${option.hanji} (${option.wordIndexId})`}
                                          >
                                            <span>
                                              {option.hanji} {option.roman} (
                                              {option.wordIndexId})
                                            </span>
                                          </Option>
                                        ))}
                                      </Select>
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => {
                                  const fieldValue = watch(
                                    `meaning.${meaningIndex}.meaningWordSynonym.${wordSynonymIndex}`
                                  ) as any;
                                  if (fieldValue && fieldValue.id) {
                                    setListDelete({
                                      ...listDelete,
                                      [fieldValue.id]: "meaningWordSynonym",
                                    });
                                    remove(wordSynonymIndex);
                                    setValue(
                                      `meaning.${meaningIndex}.meaningWordSynonym`,
                                      watch(
                                        `meaning.${meaningIndex}.meaningWordSynonym`
                                      )?.filter(
                                        (item) => item.id !== fieldValue.id
                                      )
                                    );
                                  } else {
                                    remove(wordSynonymIndex);
                                  }
                                }}
                              />
                            </Col>
                          </Row>
                          {wordSynonymIndex < wordSynonymFields.length - 1 && (
                            <div
                              style={{
                                height: 8,
                              }}
                            />
                          )}
                        </div>
                      )
                    )}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            relativeWordIndexId: "",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                        disabled={!isDictionarySelected}
                      >
                        添加 / Add
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              {/* Meaning Word Antonym */}
              <Form.List
                name={`meaning.${meaningIndex}.meaningWordAntonym`}
                initialValue={getValues(
                  `meaning.${meaningIndex}.meaningWordAntonym`
                )}
              >
                {(wordAntonymFields, { add, remove }) => (
                  <>
                    <Text>義項-詞目反義 / Definition-entry antonym(s)</Text>
                    <div style={{ height: 8 }} />
                    {(wordAntonymFields as FieldWithPossibleId[]).map(
                      (wordAntonymField, wordAntonymIndex) => (
                        <div
                          key={wordAntonymField.fieldKey}
                          style={{
                            marginBottom:
                              wordAntonymIndex === wordAntonymFields.length - 1
                                ? 16
                                : 0,
                          }}
                        >
                          <Row gutter={16} align="middle">
                            <Col flex="auto">
                              <Form.Item
                                required={false}
                                style={{
                                  marginBottom: 0,
                                }}
                              >
                                <Controller
                                  name={`meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}.relativeWordIndexId`}
                                  control={control}
                                  render={({ field }) => {
                                    const relativeHanji = watch(
                                      `meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}.relativeHanji`
                                    );
                                    const relativeRoman = watch(
                                      `meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}.relativeRoman`
                                    );

                                    const currentOption = field.value
                                      ? {
                                          wordIndexId: field.value,
                                          hanji: relativeHanji || "",
                                          roman: relativeRoman || "",
                                        }
                                      : null;

                                    const allOptions = currentOption
                                      ? [
                                          currentOption,
                                          ...(meaningWordAntonymOptions?.results.filter(
                                            (opt: WordOption) =>
                                              opt.wordIndexId !== field.value
                                          ) || []),
                                        ]
                                      : meaningWordAntonymOptions?.results ||
                                        [];

                                    return (
                                      <Select
                                        {...field}
                                        showSearch
                                        placeholder="Search for definition-entry antonym"
                                        onSearch={(value) =>
                                          handleSearch(
                                            value,
                                            "meaningWordAntonym"
                                          )
                                        }
                                        onChange={(value) => {
                                          const selectedOption =
                                            allOptions.find(
                                              (opt) => opt.wordIndexId === value
                                            );

                                          field.onChange(value);

                                          if (selectedOption) {
                                            setValue(
                                              `meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}.relativeHanji`,
                                              selectedOption.hanji
                                            );
                                            setValue(
                                              `meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}.relativeRoman`,
                                              selectedOption.roman
                                            );
                                          }
                                        }}
                                        filterOption={false}
                                        loading={
                                          isMeaningWordAntonymSearchLoading
                                        }
                                        optionLabelProp="label"
                                        disabled={!isDictionarySelected}
                                      >
                                        {allOptions.map((option) => (
                                          <Option
                                            key={option.wordIndexId}
                                            value={option.wordIndexId}
                                            label={`${option.hanji} (${option.wordIndexId})`}
                                          >
                                            <span>
                                              {option.hanji} {option.roman} (
                                              {option.wordIndexId})
                                            </span>
                                          </Option>
                                        ))}
                                      </Select>
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="text"
                                icon={<MinusCircleOutlined />}
                                onClick={() => {
                                  const fieldValue = watch(
                                    `meaning.${meaningIndex}.meaningWordAntonym.${wordAntonymIndex}`
                                  ) as any;
                                  if (fieldValue && fieldValue.id) {
                                    setListDelete({
                                      ...listDelete,
                                      [fieldValue.id]: "meaningWordAntonym",
                                    });
                                    remove(wordAntonymIndex);
                                    setValue(
                                      `meaning.${meaningIndex}.meaningWordAntonym`,
                                      watch(
                                        `meaning.${meaningIndex}.meaningWordAntonym`
                                      )?.filter(
                                        (item) => item.id !== fieldValue.id
                                      )
                                    );
                                  } else {
                                    remove(wordAntonymIndex);
                                  }
                                }}
                              />
                            </Col>
                          </Row>
                          {wordAntonymIndex < wordAntonymFields.length - 1 && (
                            <div
                              style={{
                                height: 8,
                              }}
                            />
                          )}
                        </div>
                      )
                    )}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            relativeWordIndexId: "",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                        disabled={!isDictionarySelected}
                      >
                        添加 / Add
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
              {/* Example */}
              <Form.List
                name={`meaning.${meaningIndex}.example`}
                initialValue={getValues(`meaning.${meaningIndex}.example`)}
              >
                {(fields, { add, remove }) => (
                  <>
                    <Text>例句 / Example(s)</Text>
                    <div style={{ height: 8 }} />
                    {fields.map((exampleField, exampleIndex) => (
                      <Card
                        key={exampleField.key}
                        style={{ marginBottom: 16 }}
                        title={`例句 ${exampleIndex + 1} / Example ${exampleIndex + 1}`}
                        extra={
                          <Button
                            onClick={() => {
                              const fieldValue = watch(
                                `meaning.${meaningIndex}.example.${exampleIndex}`
                              ) as any;
                              if (fieldValue && fieldValue.id) {
                                setListDelete({
                                  ...listDelete,
                                  [fieldValue.id]: "example",
                                });
                                remove(exampleIndex);
                                setValue(
                                  `meaning.${meaningIndex}.example`,
                                  watch(
                                    `meaning.${meaningIndex}.example`
                                  )?.filter((item) => item.id !== fieldValue.id)
                                );
                              } else {
                                remove(exampleIndex);
                              }
                            }}
                            icon={<MinusCircleOutlined />}
                          />
                        }
                      >
                        <Form.Item label="福建文漢字例句 / Hokkien example in Chinese characters">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleHanji`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="福建文羅馬字例句 / Hokkien example in Romanisation">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleRoman`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="華文例句 / Mandarin example">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleChinese`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="英文例句 / English example">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleEnglish`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="馬來文例句 / Malay example">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleMalay`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="印尼文例句 / Indonesian example">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleIndonesia`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                        <Form.Item label="菲律賓文例句 / Filipino example">
                          <Controller
                            name={`meaning.${meaningIndex}.example.${exampleIndex}.exampleTagalog`}
                            control={control}
                            render={({ field }) => <Input {...field} />}
                          />
                        </Form.Item>
                      </Card>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() =>
                          add({
                            exampleHanji: "",
                            exampleRoman: "",
                            exampleChinese: "",
                            exampleEnglish: "",
                            exampleMalay: "",
                            exampleTagalog: "",
                            exampleIndonesia: "",
                          })
                        }
                        block
                        icon={<PlusOutlined />}
                      >
                        添加例句 / Add an example
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Card>
          ))}

          <Form.Item>
            <Button
              type="dashed"
              onClick={() =>
                appendMeaning({
                  partOfSpeech: "",
                  meaningChinese: "",
                  meaningEnglish: "",
                  meaningHokkien: "",
                  meaningTagalog: "",
                  meaningIndonesia: "",
                  meaningMalay: "",
                  meaningSynonym: [],
                  meaningAntonym: [],
                  meaningWordSynonym: [],
                  meaningWordAntonym: [],
                  example: [],
                })
              }
              block
              icon={<PlusOutlined />}
            >
              添加義項 / Add a definition
            </Button>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          {/* Word Synonym */}
          <Text>詞目-詞目近義 / Entry-entry synonym(s)</Text>
          <div style={{ height: 8 }} />
          <Form.List name="wordSynonym">
            {() => (
              <>
                {wordSynonymFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom:
                        index === wordSynonymFields.length - 1 ? 16 : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item required={false} style={{ marginBottom: 0 }}>
                          <Tooltip
                            title={!isDictionarySelected ? tooltipTitle : ""}
                          >
                            <Controller
                              name={`wordSynonym.${index}.relativeWordIndexId`}
                              control={control}
                              render={({ field: controllerField }) => {
                                const relativeHanji = watch(
                                  `wordSynonym.${index}.relativeHanji`
                                );
                                const relativeRoman = watch(
                                  `wordSynonym.${index}.relativeRoman`
                                );
                                const currentOption = controllerField.value
                                  ? {
                                      wordIndexId: controllerField.value,
                                      hanji: relativeHanji || "",
                                      roman: relativeRoman || "",
                                    }
                                  : null;

                                const allOptions = currentOption
                                  ? [
                                      currentOption,
                                      ...(wordSynonymOptions?.results.filter(
                                        (opt: WordOption) =>
                                          opt.wordIndexId !==
                                          controllerField.value
                                      ) || []),
                                    ]
                                  : wordSynonymOptions?.results || [];

                                return (
                                  <Select
                                    {...controllerField}
                                    showSearch
                                    placeholder="Search for synonyms"
                                    onSearch={(value) =>
                                      handleSearch(value, "wordSynonym")
                                    }
                                    onChange={(value) => {
                                      const selectedOption = allOptions.find(
                                        (opt) => opt.wordIndexId === value
                                      );

                                      controllerField.onChange(value);

                                      if (selectedOption) {
                                        setValue(
                                          `wordSynonym.${index}.relativeHanji`,
                                          selectedOption.hanji
                                        );
                                        setValue(
                                          `wordSynonym.${index}.relativeRoman`,
                                          selectedOption.roman
                                        );
                                      }
                                    }}
                                    filterOption={false}
                                    loading={isWordSynonymSearchLoading}
                                    optionLabelProp="label"
                                    disabled={!isDictionarySelected}
                                  >
                                    {allOptions.map((option) => (
                                      <Option
                                        key={option.wordIndexId}
                                        value={option.wordIndexId}
                                        label={`${option.hanji} ${option.roman} (${option.wordIndexId})`}
                                      >
                                        <span>
                                          {option.hanji} {option.roman} (
                                          {option.wordIndexId})
                                        </span>
                                      </Option>
                                    ))}
                                  </Select>
                                );
                              }}
                            />
                          </Tooltip>
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(
                              `wordSynonym.${index}`
                            ) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "wordSynonym",
                              });
                              removeWordSynonym(index);
                            } else {
                              removeWordSynonym(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < wordSynonymFields.length - 1 && (
                      <div style={{ height: 8 }} />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Tooltip title={!isDictionarySelected ? tooltipTitle : ""}>
                    <Button
                      type="dashed"
                      onClick={() =>
                        appendWordSynonym({
                          relativeWordIndexId: "",
                        })
                      }
                      block
                      icon={<PlusOutlined />}
                      disabled={!isDictionarySelected}
                    >
                      添加 / Add
                    </Button>
                  </Tooltip>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Word Antonym */}
          <Form.List name="wordAntonym">
            {() => (
              <>
                <Text>詞目-詞目反義 / Entry-entry antonym(s)</Text>
                <div style={{ height: 8 }} />
                {wordAntonymFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      marginBottom:
                        index === wordAntonymFields.length - 1 ? 16 : 0,
                    }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="auto">
                        <Form.Item required={false} style={{ marginBottom: 0 }}>
                          <Tooltip
                            title={!isDictionarySelected ? tooltipTitle : ""}
                          >
                            <Controller
                              name={`wordAntonym.${index}.relativeWordIndexId`}
                              control={control}
                              render={({ field: controllerField }) => {
                                const relativeHanji = watch(
                                  `wordAntonym.${index}.relativeHanji`
                                );
                                const relativeRoman = watch(
                                  `wordAntonym.${index}.relativeRoman`
                                );
                                const currentOption = controllerField.value
                                  ? {
                                      wordIndexId: controllerField.value,
                                      hanji: relativeHanji || "",
                                      roman: relativeRoman || "",
                                    }
                                  : null;

                                const allOptions = currentOption
                                  ? [
                                      currentOption,
                                      ...(wordAntonymOptions?.results.filter(
                                        (opt: WordOption) =>
                                          opt.wordIndexId !==
                                          controllerField.value
                                      ) || []),
                                    ]
                                  : wordAntonymOptions?.results || [];
                                return (
                                  <Select
                                    {...controllerField}
                                    showSearch
                                    placeholder="Search for antonyms"
                                    onSearch={(value) =>
                                      handleSearch(value, "wordAntonym")
                                    }
                                    onChange={(value) => {
                                      const selectedOption = allOptions.find(
                                        (opt) => opt.wordIndexId === value
                                      );

                                      controllerField.onChange(value);

                                      if (selectedOption) {
                                        setValue(
                                          `wordAntonym.${index}.relativeHanji`,
                                          selectedOption.hanji
                                        );
                                        setValue(
                                          `wordAntonym.${index}.relativeRoman`,
                                          selectedOption.roman
                                        );
                                      }
                                    }}
                                    filterOption={false}
                                    loading={isWordAntonymSearchLoading}
                                    optionLabelProp="label"
                                    disabled={!isDictionarySelected}
                                  >
                                    {allOptions.map((option) => (
                                      <Option
                                        key={option.wordIndexId}
                                        value={option.wordIndexId}
                                        label={`${option.hanji} ${option.roman} (${option.wordIndexId})`}
                                      >
                                        <span>
                                          {option.hanji} {option.roman} (
                                          {option.wordIndexId})
                                        </span>
                                      </Option>
                                    ))}
                                  </Select>
                                );
                              }}
                            />
                          </Tooltip>
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            const fieldValue = watch(
                              `wordAntonym.${index}`
                            ) as any;
                            if (fieldValue && fieldValue.id) {
                              setListDelete({
                                ...listDelete,
                                [fieldValue.id]: "wordAntonym",
                              });
                              removeWordAntonym(index);
                            } else {
                              removeWordAntonym(index);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                    {index < wordAntonymFields.length - 1 && (
                      <div style={{ height: 8 }} />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Tooltip title={!isDictionarySelected ? tooltipTitle : ""}>
                    <Button
                      type="dashed"
                      onClick={() =>
                        appendWordAntonym({
                          relativeWordIndexId: "",
                        })
                      }
                      block
                      icon={<PlusOutlined />}
                      disabled={!isDictionarySelected}
                    >
                      添加 / Add
                    </Button>
                  </Tooltip>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* Accent */}
          {renderHeading({ title: "語音差異 / Accents" })}
          <Card>
            {newAccentFields.map((field, index) => (
              <Row style={{ width: "100%" }}>
                <Col style={{ flexGrow: "1" }}>
                  <Space
                    key={field.id}
                    style={{ display: "flex", marginBottom: 8 }}
                    direction="vertical"
                  >
                    <Form.Item
                      validateStatus={
                        errors.newAccentField?.[index]?.region ? "error" : ""
                      }
                      help={errors.newAccentField?.[index]?.region?.message}
                      style={{ marginBottom: 0 }}
                    >
                      <Controller
                        name={`newAccentField.${index}.region`}
                        control={control}
                        rules={{ required: "Enter Accent Region" }}
                        render={({ field: controllerField }) => (
                          <Input
                            {...controllerField}
                            placeholder="地區 / Locale"
                            status={
                              errors.newAccentField?.[index]?.region
                                ? "error"
                                : ""
                            }
                          />
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      validateStatus={
                        errors.newAccentField?.[index]?.accentValue
                          ? "error"
                          : ""
                      }
                      help={
                        errors.newAccentField?.[index]?.accentValue?.message
                      }
                    >
                      <Controller
                        name={`newAccentField.${index}.accentValue`}
                        control={control}
                        rules={{ required: "Enter Accent Pronunciation" }}
                        render={({ field: controllerField }) => (
                          <Input
                            {...controllerField}
                            placeholder="發音 / Pronunciation"
                            status={
                              errors.newAccentField?.[index]?.accentValue
                                ? "error"
                                : ""
                            }
                          />
                        )}
                      />
                    </Form.Item>
                  </Space>
                </Col>
                <Col>
                  <Button
                    type="text"
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeNewAccent(index)}
                  />
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => appendNewAccent({ region: "", accentValue: "" })}
                block
                icon={<PlusOutlined />}
              >
                添加語音差異 / Add an accent
              </Button>
            </Form.Item>
          </Card>
          <div style={{ height: 16 }} />
          {renderHeading({ title: "上傳錄音 / Upload a recording" })}
          <Form.Item>
            <Controller
              name="sound"
              control={control}
              render={({ field }) => (
                <>
                  <Dragger
                    beforeUpload={handleSoundUpload}
                    onRemove={handleSoundRemove}
                    fileList={soundFile ? [soundFile] : []}
                    multiple={false}
                    accept="audio/webm,video/webm"
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p>點擊或拖放一個 WebM 文件以上傳。</p>
                    <p>Click or drag a WebM file to this area to upload.</p>
                    <p className="ant-upload-hint">
                      文件必須是50MB以下的 WebM 格式。
                    </p>
                    <p className="ant-upload-hint">
                      The file must be smaller than 50 MB in WebM format.
                    </p>
                  </Dragger>
                  {fileInfo && (
                    <div style={{ marginTop: "8px" }}>
                      <Text>File Size: {fileInfo.size}</Text>
                      <br />
                      <Text>File Type: {fileInfo.type}</Text>
                    </div>
                  )}
                </>
              )}
            />
          </Form.Item>

          {currentSoundUrl && (
            <Form.Item>
              {" "}
              <audio
                ref={audioRef}
                src={currentSoundUrl}
                style={{ display: "none" }}
              />
              <Space>
                <Button
                  onClick={handlePlaySound}
                  icon={
                    isPlaying ? (
                      <StopIcon style={{ width: "12px", height: "12px" }} />
                    ) : (
                      <PlayCircleOutlined />
                    )
                  }
                >
                  {isPlaying
                    ? "停止播放錄音 / Stop recording"
                    : "播放錄音 / Play recording"}
                </Button>
                <Button
                  onClick={handleDownloadSound}
                  icon={<DownloadOutlined />}
                  loading={isDownloading}
                >
                  下載 / Download
                </Button>
              </Space>
            </Form.Item>
          )}
        </Col>
      </Row>
      {duplicateError && duplicateError.length > 0 && (
        <Alert
          message="Duplicate Entries Detected"
          description={
            <div>
              <p>The following duplicate entries were found:</p>
              <ul>
                {duplicateError.map((error, index) => (
                  <li key={index}>
                    Duplicate {error.type} detected in{" "}
                    <strong>{error.field}</strong>:
                    <br />
                    {error.type === "Word Synonym" ||
                    error.type === "Word Antonym" ? (
                      <span>
                        Word with index ID <strong>{error.value}</strong> is
                        already used.
                      </span>
                    ) : error.type.startsWith("Meaning") ? (
                      <span>
                        Meaning or word with index ID{" "}
                        <strong>{error.value}</strong> is already used in this
                        section.
                      </span>
                    ) : (
                      <span>
                        Entry <strong>{error.value}</strong> is duplicated.
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p>
                Please remove or change the duplicate entries before submitting.
                Each entry should be unique within its category.
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={createMutation.isPending || editMutation.isPending}
          >
            {isNewVocab ? "創建 / Create" : "更新 / Update"}
          </Button>
          <Button onClick={() => navigate(-1)}>取消 / Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default VocabAddEdit;
